<template>
  <div class="select-contract">
    <ScoreBar />

    <div ref="content" :class="['content', { 'content--select': !selectedContract, 'content--players-only': selectedContract && !needsSuit && !needsTricks }]">
      <!-- Screen 1: Contract type selection -->
      <template v-if="!selectedContract">
        <div class="contract-select-column">
          <h1 class="screen-title">Kies contract</h1>
          <ul class="contract-list" role="radiogroup">
            <li
              v-for="c in contracts"
              :key="c.id"
              class="contract-item"
              :class="{ selected: highlightedContract === c.id }"
              tabindex="0"
              role="radio"
              :aria-checked="highlightedContract === c.id"
              @click="highlightedContract = c.id"
              @keydown.enter.stop="highlightedContract = c.id; confirmContractChoice()"
              @keydown.space.prevent="highlightedContract = c.id"
            >
              <span class="radio" />
              <span>{{ c.label }}</span>
            </li>
          </ul>
        </div>

        <aside class="contract-info" aria-label="Contract informatie">
          <section class="info-section">
            <h2 class="section-title">Troef volgorde (hoog naar laag)</h2>
            <ol class="suit-order-list">
              <li v-for="suit in suits" :key="suit.id" class="suit-order-item">
                <span class="suit-order-symbol" :class="{ red: suit.red }">{{ suit.symbol }}</span>
                <span>{{ suit.label }}</span>
              </li>
            </ol>
          </section>

          <section class="info-section">
            <h2 class="section-title">Contracten</h2>
            <div class="table-wrapper">
              <table class="contract-info-table">
                <thead>
                  <tr>
                    <th scope="col">Contract</th>
                    <th scope="col">Spelers</th>
                    <th scope="col">Troef</th>
                    <th scope="col">Slagen</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in contractInfoRows" :key="row.id">
                    <th scope="row">{{ row.label }}</th>
                    <td>{{ row.playersLabel }}</td>
                    <td>{{ row.suit ? 'Ja' : '–' }}</td>
                    <td>{{ row.tricksLabel }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </aside>
      </template>

      <!-- Screen 2: Follow-up questions (all on one page) -->
      <template v-else>
        <div class="chosen-contract">
          <span class="chosen-label">{{ selectedContractLabel }}</span>
        </div>

        <!-- Player selection -->
        <section class="section">
          <h2 class="section-title">{{ playerSectionTitle }}</h2>
          <ul
            :class="['player-list', { 'player-list--grid': !needsSuit && !needsTricks }]"
            :role="isSinglePlayerSelect ? 'radiogroup' : 'group'"
            :aria-label="playerSectionTitle"
          >
            <li
              v-for="(player, index) in players"
              :key="player.id"
              class="player-item"
              :class="{ selected: selectedPlayerIds.includes(player.id) }"
              tabindex="0"
              :role="isSinglePlayerSelect ? 'radio' : 'checkbox'"
              :aria-checked="selectedPlayerIds.includes(player.id)"
              @click="togglePlayer(player.id)"
              @keydown.space.prevent="togglePlayer(player.id)"
            >
              <span class="player-label">Speler {{ index + 1 }}</span>
              <span class="player-name">{{ player.name }}</span>
            </li>
          </ul>
        </section>

        <!-- Trump suit + tricks (grouped so they land in the same column in landscape) -->
        <div v-if="needsSuit || needsTricks" class="right-sections">
          <section v-if="needsSuit" class="section">
            <h2 class="section-title">Troef</h2>
            <div class="suit-grid" :style="{ gridTemplateColumns: `repeat(${Math.ceil(suits.length / 2)}, 1fr)` }">
              <button
                v-for="suit in suits"
                :key="suit.id"
                class="suit-chip"
                :class="{ selected: selectedSuit === suit.id, red: suit.red }"
                @click="selectedSuit = suit.id"
              >
                <span class="suit-symbol">{{ suit.symbol }}</span>
                <span class="suit-name">{{ suit.label }}</span>
              </button>
            </div>
          </section>

          <section v-if="needsTricks" class="section">
            <h2 class="section-title">Slagen</h2>
            <div class="tricks-grid" :style="{ gridTemplateColumns: `repeat(${Math.ceil(tricksRange.length / 2)}, 1fr)` }">
              <button
                v-for="n in tricksRange"
                :key="n"
                class="tricks-chip"
                :class="{ selected: selectedTricks === n }"
                @click="selectedTricks = n"
              >
                {{ n }}
              </button>
            </div>
          </section>
        </div>
      </template>
    </div>

    <AppFooter
      :show-back="true"
      :continue-label="selectedContract ? 'Bevestigen' : 'Volgende'"
      :can-continue="selectedContract ? canConfirm : highlightedContract !== null"
      @back="goBack"
      @continue="goForward"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia'
import { useGameStore } from '../../stores/game'
import { useContractStore } from '../../stores/contract'
import { SUITS } from '../../models/suits'
import ScoreBar from '../shared/ScoreBar.vue'
import AppFooter from '../shared/AppFooter.vue'

const CONTRACTS = [
  { id: 'samen',          label: 'Samen' },
  { id: 'alleen',         label: 'Alleen' },
  { id: 'kleine-miserie', label: 'Kleine miserie' },
  { id: 'piccolo',        label: 'Piccolo' },
  { id: 'troel',          label: 'Troel' },
  { id: 'abondance',      label: 'Abondance' },
  { id: 'grote-miserie',  label: 'Grote miserie' },
  { id: 'open-miserie',   label: 'Open miserie' },
  { id: 'solo-slim',      label: 'Solo slim' },
]

const CONTRACT_CONFIG = {
  'samen':          { players: { exact: 2 }, suit: true,  tricks: { min: 8, max: 13 } },
  'alleen':         { players: { exact: 1 }, suit: true,  tricks: { min: 5, max: 8 } },
  'kleine-miserie': { players: { min: 1 },   suit: false, tricks: null },
  'piccolo':        { players: { min: 1 },   suit: false, tricks: null },
  'troel':          { players: { exact: 2 }, suit: true,  tricks: { min: 8, max: 9 } },
  'abondance':      { players: { exact: 1 }, suit: true,  tricks: { min: 9, max: 12 } },
  'grote-miserie':  { players: { min: 1 },   suit: false, tricks: null },
  'open-miserie':   { players: { min: 1 },   suit: false, tricks: null },
  'solo-slim':      { players: { exact: 1 }, suit: true,  tricks: null },
}

export default {
  name: 'SelectContract',
  components: { ScoreBar, AppFooter },

  data() {
    return {
      highlightedContract: null,
      selectedContract: null,
      selectedPlayerIds: [],
      selectedSuit: null,
      selectedTricks: null,
      contracts: CONTRACTS,
      suits: SUITS,
    }
  },

  computed: {
    ...mapState(useGameStore, ['players']),

    selectedContractLabel() {
      const c = CONTRACTS.find(c => c.id === this.selectedContract)
      return c ? c.label : ''
    },

    config() {
      return CONTRACT_CONFIG[this.selectedContract] || {}
    },

    playerRule() {
      return this.config.players || {}
    },

    isSinglePlayerSelect() {
      return this.playerRule.exact === 1
    },

    needsSuit() {
      return !!this.config.suit
    },

    needsTricks() {
      return this.config.tricks !== null && this.config.tricks !== undefined
    },

    playerSectionTitle() {
      const rule = this.playerRule
      if (rule.exact === 1) return 'Speler'
      if (rule.exact === 2) return 'Spelers (kies 2)'
      return 'Spelers'
    },

    tricksRange() {
      const range = this.config.tricks
      if (!range) return []
      const result = []
      for (let i = range.min; i <= range.max; i++) {
        result.push(i)
      }
      return result
    },

    contractInfoRows() {
      const rows = []
      const push = (id, overrides = {}) => {
        const cfg = CONTRACT_CONFIG[id]
        const c = this.contracts.find(c => c.id === id)
        rows.push({
          id: overrides.id || id,
          label: overrides.label || c.label,
          suit: cfg.suit,
          playersLabel: cfg.players.exact ? `${cfg.players.exact}` : `${cfg.players.min}+`,
          tricksLabel: overrides.tricksLabel !== undefined
            ? overrides.tricksLabel
            : (cfg.tricks ? `${cfg.tricks.min}–${cfg.tricks.max}` : '–'),
        })
      }

      // Abondance's rank relative to Troel depends on the bid: Abondance-9
      // ranks below Troel, Abondance-10 and higher rank above it.
      push('samen')
      push('alleen')
      push('kleine-miserie')
      push('piccolo')
      push('abondance', { id: 'abondance-9', tricksLabel: '9' })
      push('troel')
      push('abondance', { id: 'abondance-10-12', tricksLabel: '10–12' })
      push('grote-miserie')
      push('open-miserie')
      push('solo-slim')

      return rows
    },

    isPlayerSelectionValid() {
      const rule = this.playerRule
      const count = this.selectedPlayerIds.length
      if (rule.exact) return count === rule.exact
      if (rule.min) return count >= rule.min
      return count > 0
    },

    canConfirm() {
      if (!this.selectedContract) return false
      if (!this.isPlayerSelectionValid) return false
      if (this.needsSuit && !this.selectedSuit) return false
      if (this.needsTricks && this.selectedTricks === null) return false
      return true
    },
  },

  methods: {
    ...mapActions(useGameStore, ['cancelRound', 'startPlaying']),

    confirmContractChoice() {
      if (!this.highlightedContract) return
      this.selectedContract = this.highlightedContract
      this.selectedPlayerIds = []
      this.selectedSuit = null
      this.selectedTricks = null
      this.$refs.content.scrollTop = 0
    },

    clearContract() {
      this.selectedContract = null
      this.selectedPlayerIds = []
      this.selectedSuit = null
      this.selectedTricks = null
      this.$refs.content.scrollTop = 0
    },

    goForward() {
      if (!this.selectedContract) {
        this.confirmContractChoice()
      } else {
        this.submitContract()
      }
    },

    togglePlayer(playerId) {
      const rule = this.playerRule
      const idx = this.selectedPlayerIds.indexOf(playerId)

      if (idx !== -1) {
        this.selectedPlayerIds.splice(idx, 1)
        return
      }

      if (rule.exact === 1) {
        this.selectedPlayerIds = [playerId]
      } else if (rule.exact === 2) {
        if (this.selectedPlayerIds.length >= 2) {
          this.selectedPlayerIds = [this.selectedPlayerIds[1], playerId]
        } else {
          this.selectedPlayerIds.push(playerId)
        }
      } else {
        this.selectedPlayerIds.push(playerId)
      }
    },

    goBack() {
      if (this.selectedContract) {
        this.clearContract()
      } else {
        this.cancelRound()
      }
    },

    submitContract() {
      if (!this.canConfirm) return

      const contractStore = useContractStore()
      const suitObj = this.selectedSuit
        ? SUITS.find(s => s.id === this.selectedSuit)
        : null

      contractStore.saveContractState({
        type: this.selectedContract,
        label: this.selectedContractLabel,
        playerIds: [...this.selectedPlayerIds],
        suit: this.selectedSuit,
        suitSymbol: suitObj ? suitObj.symbol : null,
        suitRed: suitObj ? suitObj.red : false,
        tricks: this.selectedTricks,
      })

      this.startPlaying()
    },
  },
}
</script>

<style scoped>
.select-contract {
  display: flex;
  flex-direction: column;
  height: 100%;
}

/* Shared content area */
.content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-y: contain;
  padding: 1rem;
  gap: 0.75rem;
}

/* Screen 2 with trump/tricks: 1/3 players, 2/3 trump+tricks */
.content:not(.content--select):not(.content--players-only) {
  display: grid;
  grid-template-columns: 1fr 2fr;
  grid-template-rows: auto 1fr;
  align-items: stretch;
}

/* Screen 2 players only: centered like dealer select */
.content--players-only {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: safe center;
}

.content--players-only .chosen-contract,
.content--players-only .section {
  width: 100%;
  max-width: min(600px, 100%);
  flex: 0 0 auto;
}

/* Screen 1: contract list, then info panel below (stacked on phones) */
.content--select {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

@media (min-width: 600px) {
  /* From tablet width up: 1/3 contract list, 2/3 info panel side by side */
  .content--select {
    display: grid;
    grid-template-columns: 1fr 2fr;
    grid-template-rows: 1fr;
    align-items: stretch;
    gap: 1.5rem;
  }
}

/* Screen 1 */
.contract-select-column {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-width: 0;
}

@media (min-width: 600px) {
  .contract-select-column {
    height: 100%;
  }
}

.screen-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #2c3e50;
}

.contract-list {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  width: 100%;
}

.contract-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.7rem 1rem;
  border: 2px solid #aac8aa;
  border-radius: 8px;
  background: #fff;
  color: #2c3e50;
  font-size: 0.95rem;
  cursor: pointer;
  touch-action: manipulation;
  transition: border-color 0.15s, background 0.15s;
}

.contract-item.selected {
  border-color: #2c3e50;
  background: #2c3e50;
  color: #fff;
}

.radio {
  flex-shrink: 0;
  width: 1.1rem;
  height: 1.1rem;
  border-radius: 50%;
  border: 2px solid #aac8aa;
  background: #fff;
  transition: border-color 0.15s, background 0.15s;
}

.selected .radio {
  border-color: #fff;
  background: #fff;
  box-shadow: inset 0 0 0 3px #2c3e50;
}

/* Screen 1: info panel */
.contract-info {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 0;
}

@media (min-width: 600px) {
  .contract-info {
    height: 100%;
    overflow-y: auto;
  }
}

.info-section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.suit-order-list {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.suit-order-item {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.4rem 0.6rem;
  border: 1px solid #aac8aa;
  border-radius: 8px;
  background: #fff;
  color: #2c3e50;
}

.suit-order-symbol {
  font-size: 1.2rem;
  line-height: 1;
}

.suit-order-symbol.red {
  color: #c0392b;
}

.table-wrapper {
  overflow-x: auto;
}

.contract-info-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
}

.contract-info-table th,
.contract-info-table td {
  padding: 0.4rem 0.5rem;
  text-align: left;
  border-bottom: 1px solid #aac8aa;
  white-space: nowrap;
}

.contract-info-table thead th {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #7a8a7a;
}

.contract-info-table tbody th {
  font-weight: 600;
  color: #2c3e50;
}

/* Screen 2: chosen contract badge — spans all grid columns */
.chosen-contract {
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chosen-label {
  font-size: 1.15rem;
  font-weight: 700;
  color: #fff;
  background-color: #2c3e50;
  padding: 0.3em 0.9em;
  border-radius: 999px;
}

/* Right column: suit + tricks grouped */
.right-sections {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  height: 100%;
}

.section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
}

.section-title {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #7a8a7a;
  padding-left: 0.25rem;
}

.player-list {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  height: 100%;
}

/* Players-only mode: 2x2 grid like dealer select */
.player-list--grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  height: auto;
  width: 100%;
  max-width: min(600px, 100%);
}

.player-item {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.25rem;
  padding: 0.75rem 1rem;
  border: 2px solid #aac8aa;
  border-radius: 8px;
  background: #fff;
  color: #2c3e50;
  cursor: pointer;
  touch-action: manipulation;
  transition: border-color 0.15s, background 0.15s;
}

.player-item.selected {
  border-color: #2c3e50;
  background: #2c3e50;
  color: #fff;
}

.player-label {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  opacity: 0.6;
}

.player-name {
  font-size: 1rem;
  font-weight: 600;
}

.suit-grid {
  flex: 1;
  display: grid;
  grid-template-rows: repeat(2, 1fr);
  gap: 0.35rem;
}

.suit-chip {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.2rem;
  padding: 0.25rem;
  font-family: inherit;
  border: 2px solid transparent;
  border-radius: 10px;
  background: #fff;
  color: #2c3e50;
  cursor: pointer;
  touch-action: manipulation;
  transition: border-color 0.15s, background-color 0.15s, box-shadow 0.15s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.suit-chip.selected {
  border-color: #2c3e50;
  background-color: #eaf2ea;
  box-shadow: 0 2px 8px rgba(44, 62, 80, 0.2);
}

.suit-chip.red .suit-symbol { color: #c0392b; }
.suit-chip:active { transform: scale(0.97); }

.suit-symbol {
  font-size: 2rem;
  line-height: 1;
}

.suit-name {
  font-size: 0.7rem;
  font-weight: 600;
}

.tricks-grid {
  flex: 1;
  display: grid;
  grid-template-rows: repeat(2, 1fr);
  gap: 0.35rem;
}

.tricks-chip {
  padding: 0;
  font-size: 1.05rem;
  font-family: inherit;
  font-weight: 700;
  border: 2px solid transparent;
  border-radius: 10px;
  background: #fff;
  color: #2c3e50;
  cursor: pointer;
  touch-action: manipulation;
  text-align: center;
  transition: border-color 0.15s, background-color 0.15s, box-shadow 0.15s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.tricks-chip.selected {
  border-color: #2c3e50;
  background-color: #2c3e50;
  color: #fff;
  box-shadow: 0 2px 8px rgba(44, 62, 80, 0.3);
}

.tricks-chip:active { transform: scale(0.97); }

@media (hover: hover) {
  .contract-item:hover,
  .player-item:hover { border-color: #7a9a7a; }
  .suit-chip:hover { border-color: #7a9a7a; }
  .tricks-chip:hover { border-color: #7a9a7a; }
}
</style>
